/**
 * Meta Pixel (Facebook Pixel) Tracking Utility
 * Tracks page views and events on public pages
 */

const META_PIXEL_ID = '4432238850341890'

/**
 * List of public page paths where Meta Pixel should track
 */
const PUBLIC_PAGES = [
  '/',
  '/pricing',
  '/features',
  '/faqs',
  '/about',
  '/blog',
  '/prosperia-ai',
  '/pwa-welcome',
  '/privacy',
  '/terms',
  '/eula',
  '/cookies',
  // Feature detail pages
  '/features/estimates-invoices',
  '/features/client-management',
  '/features/job-tracking',
  '/features/employee-management',
  '/features/mobile-app',
  '/features/payment-processing',
  '/features/embeddable-widgets',
  '/features/scheduling-calendar',
  '/features/automations',
  '/features/file-management',
  '/features/ai-powered-tools',
  '/features/site-analysis-tools',
  '/features/services-packages',
  '/features/expense-management',
  '/features/reports-analytics',
  '/features/tool-belt'
]

/**
 * Check if the current path is a public page
 * @param {string} pathname - Current pathname
 * @returns {boolean}
 */
export const isPublicPage = (pathname) => {
  // Check exact matches
  if (PUBLIC_PAGES.includes(pathname)) {
    return true
  }
  
  // Check if it's a public route (starts with /public/)
  if (pathname.startsWith('/public/')) {
    return true
  }
  
  // Check if it's a payment success page
  if (pathname.startsWith('/payment/success')) {
    return true
  }
  
  // Check if it's email verification page
  if (pathname.startsWith('/email-verification')) {
    return true
  }
  
  // Check if it's reset password page
  if (pathname.startsWith('/reset-password')) {
    return true
  }
  
  // Check if it's onboarding page (public signup flow)
  if (pathname.startsWith('/onboarding')) {
    return true
  }
  
  // Check if it's a features sub page (catches all /features/* routes)
  if (pathname.startsWith('/features/')) {
    return true
  }
  
  return false
}

/**
 * Check if Meta Pixel (fbq) is loaded
 * @returns {boolean}
 */
const isMetaPixelLoaded = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function'
}

/**
 * Track a page view on Meta Pixel
 * Only tracks if on a public page
 * @param {string} pathname - Current pathname
 */
export const trackMetaPixelPageView = (pathname) => {
  // Only track on public pages
  if (!isPublicPage(pathname)) {
    return
  }
  
  // Check if Meta Pixel is loaded
  if (isMetaPixelLoaded()) {
    try {
      window.fbq('track', 'PageView')
      console.log('[Meta Pixel] Page view tracked:', pathname)
    } catch (error) {
      console.error('Error tracking Meta Pixel PageView:', error)
    }
  } else {
    console.warn('[Meta Pixel] fbq not loaded yet')
  }
}

/**
 * Track a custom event on Meta Pixel
 * @param {string} eventName - Name of the event (e.g., 'Lead', 'CompleteRegistration', 'Contact')
 * @param {object} eventData - Event data/parameters (optional)
 */
export const trackMetaPixelEvent = (eventName, eventData = {}) => {
  if (!isMetaPixelLoaded()) {
    console.warn('[Meta Pixel] fbq not loaded yet, event not tracked:', eventName)
    return
  }
  
  try {
    if (Object.keys(eventData).length > 0) {
      window.fbq('track', eventName, eventData)
    } else {
      window.fbq('track', eventName)
    }
    console.log('[Meta Pixel] Event tracked:', eventName, eventData)
  } catch (error) {
    console.error('Error tracking Meta Pixel event:', error)
  }
}

/**
 * Track a sign up event
 * @param {object} options - Sign up data
 * @param {string} options.source - Source of sign up (e.g., 'website', 'landing_page')
 * @param {string} options.email - User email (optional, for advanced matching)
 */
export const trackMetaPixelSignUp = ({ source = 'website', email = null }) => {
  const eventData = {
    content_name: 'User Sign Up',
    content_category: 'Registration',
    source
  }
  
  // Add email for advanced matching if provided
  if (email) {
    eventData.em = email // Meta Pixel uses 'em' for email hashing
  }
  
  trackMetaPixelEvent('CompleteRegistration', eventData)
}

/**
 * Track a demo request event
 * @param {object} options - Demo request data
 * @param {string} options.source - Source of demo request (e.g., 'landing_page', 'modal')
 * @param {string} options.email - User email (optional, for advanced matching)
 */
export const trackMetaPixelDemoRequest = ({ source = 'unknown', email = null }) => {
  const eventData = {
    content_name: 'Demo Request',
    content_category: 'Lead',
    source
  }
  
  // Add email for advanced matching if provided
  if (email) {
    eventData.em = email
  }
  
  trackMetaPixelEvent('Lead', eventData)
}

/**
 * Track a page navigation event
 * @param {string} pathname - Current pathname
 * @param {string} pageTitle - Page title (optional)
 */
export const trackMetaPixelPageNavigation = (pathname, pageTitle = null) => {
  const eventData = {
    content_name: pageTitle || pathname,
    content_category: 'Page Navigation'
  }
  
  trackMetaPixelEvent('ViewContent', eventData)
}

/**
 * Track a trial start event
 * @param {object} options - Trial start data
 * @param {string} options.source - Source of trial start
 */
export const trackMetaPixelTrialStart = ({ source = 'unknown' }) => {
  const eventData = {
    content_name: 'Trial Start',
    content_category: 'Trial',
    source
  }
  
  trackMetaPixelEvent('StartTrial', eventData)
}

/**
 * Track a contact/lead event
 * @param {object} options - Contact data
 * @param {string} options.source - Source of contact
 * @param {string} options.email - User email (optional)
 */
export const trackMetaPixelContact = ({ source = 'unknown', email = null }) => {
  const eventData = {
    content_name: 'Contact',
    content_category: 'Lead',
    source
  }
  
  if (email) {
    eventData.em = email
  }
  
  trackMetaPixelEvent('Contact', eventData)
}

/**
 * Track a purchase event
 * @param {object} options - Purchase data
 * @param {number} options.value - Purchase value/amount
 * @param {string} options.currency - Currency code (e.g., 'USD')
 * @param {string} options.contentName - Product/service name (optional)
 * @param {string} options.contentCategory - Product category (optional)
 * @param {string} options.email - User email (optional, for advanced matching)
 * @param {string} options.planId - Plan ID (optional)
 * @param {string} options.planName - Plan name (optional)
 */
export const trackMetaPixelPurchase = ({ 
  value, 
  currency = 'USD', 
  contentName = null,
  contentCategory = 'Subscription',
  email = null,
  planId = null,
  planName = null
}) => {
  const eventData = {
    value: value,
    currency: currency,
    content_category: contentCategory
  }
  
  if (contentName) {
    eventData.content_name = contentName
  }
  
  if (planId) {
    eventData.content_ids = [planId]
  }
  
  if (planName) {
    eventData.content_name = planName
  }
  
  // Add email for advanced matching if provided
  if (email) {
    eventData.em = email
  }
  
  trackMetaPixelEvent('Purchase', eventData)
}

